// Owns the webcam and the detection loop, and publishes what it sees. It makes no decisions —
// face-auth-policy.js consumes these observations and decides whether to switch, lock, or sit
// still. Split that way because "who is at the monitor" is a fact worth testing and displaying
// (settings shows a live preview of it) independently of what we do about it.

import { openFaceCameraStream } from "@/lib/face/camera-access";
import {
  detectFaces,
  isFaceAtMonitor,
  isFaceRealAndLive,
  loadFaceEngine,
  matchDescriptor,
} from "@/lib/face/face-engine";
import { getEnrollments } from "@/lib/face/face-enrollment-store";

const OBSERVATION_EVENT = "healo-face-observation";
const STATE_CHANGE_EVENT = "healo-face-watch-state-change";

// Gap BETWEEN detections, by what the app is doing:
//
// - "fast" (lock screen): someone is stood there waiting to be let in, nothing else is using the
//   machine, and every millisecond is one they spend staring at a login box. Run back to back.
// - "calm" (signed in): ~2/sec. This runs for a whole shift behind real work, and nothing here
//   is urgent — the policy needs several agreeing frames before it acts anyway.
//
// The old fixed 500ms applied lock-screen latency budgeting to a background task and background
// latency budgeting to the one moment a human is actually waiting. It had it exactly backwards.
const PACE_MS = Object.freeze({ fast: 0, calm: 500 });
// Backing off after a detect failure stops a broken camera from spinning the loop at full rate.
const ERROR_BACKOFF_MS = 3000;
// A detect slower than this gets logged (throttled). Face unlock feeling slow is nearly always
// this number being large, and it is otherwise invisible from the outside.
const SLOW_DETECT_LOG_MS = 400;
const SLOW_DETECT_LOG_THROTTLE_MS = 10_000;

let status = "off"; // off | starting | watching | error
let errorReason = "";
let stream = null;
let videoEl = null;
let paceMs = PACE_MS.calm;
let lastSlowLogAt = 0;
// Bumped on every stop()/start(); an in-flight detect compares against it before publishing so
// a slow frame captured before the camera was switched off can't land afterwards.
let runToken = 0;
let lastObservation = null;
// Enrollment drives the camera itself, so the watcher steps aside while its dialog is open
// rather than fighting it for the device.
let suspendCount = 0;

function emit(eventName, detail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(eventName, { detail }));
}

function setState(nextStatus, reason = "") {
  if (status === nextStatus && errorReason === reason) return;
  status = nextStatus;
  errorReason = reason;
  emit(STATE_CHANGE_EVENT, { status, errorReason });
}

/** {status, errorReason} — 'off' | 'starting' | 'watching' | 'error'. */
export function getFaceWatchState() {
  return { status, errorReason };
}

export function subscribeFaceWatchState(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") return () => {};
  const handler = () => listener(getFaceWatchState());
  window.addEventListener(STATE_CHANGE_EVENT, handler);
  return () => window.removeEventListener(STATE_CHANGE_EVENT, handler);
}

/** The most recent observation, or null. Lets a late subscriber render without waiting a tick. */
export function getLastFaceObservation() {
  return lastObservation;
}

/**
 * The live MediaStream, or null when not watching. Shared so on-screen previews can attach the
 * SAME stream to their own <video> rather than calling getUserMedia again: a second open would
 * fight this one for the device and, on some machines, simply fail. One camera, one stream,
 * many viewers.
 *
 * Read it fresh on every watch-state change — the stream identity is replaced on each restart.
 */
export function getFaceWatchStream() {
  return stream;
}

export function subscribeFaceObservations(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") return () => {};
  const handler = (event) => listener(event.detail);
  window.addEventListener(OBSERVATION_EVENT, handler);
  return () => window.removeEventListener(OBSERVATION_EVENT, handler);
}

// A detached <video> is throttled by Chromium's rendering heuristics and can stall out, so the
// element is parented but parked off-screen. NOT display:none — that suspends decoding, which
// is exactly the "camera silently stops seeing anyone" failure this feature must never have.
function ensureVideoElement() {
  if (videoEl) return videoEl;
  videoEl = document.createElement("video");
  videoEl.muted = true;
  videoEl.playsInline = true;
  videoEl.setAttribute("aria-hidden", "true");
  videoEl.style.cssText =
    "position:fixed;top:-9999px;left:-9999px;width:64px;height:48px;opacity:0;pointer-events:none;";
  document.body.appendChild(videoEl);
  return videoEl;
}

function releaseCamera() {
  if (stream) {
    // Every track must be stopped or the camera indicator light stays on — which reads to the
    // user as "it's still watching me after I turned it off".
    for (const track of stream.getTracks()) track.stop();
    stream = null;
  }
  if (videoEl) {
    videoEl.srcObject = null;
    videoEl.remove();
    videoEl = null;
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function detectOnce(token) {
  if (!videoEl || videoEl.readyState < 2) return;
  const startedAt = performance.now();
  try {
    const faces = await detectFaces(videoEl);
    if (token !== runToken) return;

    const detectMs = Math.round(performance.now() - startedAt);
    // The one number that explains "face unlock feels slow", and invisible without saying so.
    // Throttled: it would otherwise print twice a second forever on a machine that is merely
    // a bit slow.
    if (detectMs > SLOW_DETECT_LOG_MS && Date.now() - lastSlowLogAt > SLOW_DETECT_LOG_THROTTLE_MS) {
      lastSlowLogAt = Date.now();
      console.warn(`[Face] slow detection: ${detectMs}ms/frame (${faces.length} face(s))`);
    }

    const enrollments = getEnrollments();
    const observed = faces.map((face) => {
      const match = matchDescriptor(face.embedding, enrollments);
      return {
        userId: match?.userId || null,
        similarity: match?.similarity || 0,
        real: face.real,
        live: face.live,
        sizePx: face.sizePx,
        // Precomputed so the policy never re-derives the spoof/proximity rules; it decides
        // *what to do* about a face, not *what counts as* one being present or genuine.
        isAtMonitor: isFaceAtMonitor(face),
        isRealPerson: isFaceRealAndLive(face),
        box: face.box,
        boxRaw: face.boxRaw,
      };
    });

    lastObservation = { faces: observed, at: Date.now(), detectMs };
    emit(OBSERVATION_EVENT, lastObservation);
  } catch (error) {
    if (token !== runToken) return;
    console.error("[Face] detection failed:", error);
    // Keep watching: a single bad frame (a resize, a GPU context blip) must not permanently
    // disable a security control. The backoff keeps a persistent failure cheap.
    await sleep(ERROR_BACKOFF_MS);
  }
}

/**
 * The detection loop. A self-scheduling loop rather than setInterval, because the pace changes
 * at runtime (see setFaceWatchPace) and an interval would keep firing at whatever rate it was
 * created with. It also cannot stack: the next detect is scheduled only once the last finished,
 * so a slow machine degrades to a lower frame rate instead of queueing work it can't keep up
 * with. `token` ends the loop on stop/restart without any timer to clear.
 */
async function runDetectLoop(token) {
  while (token === runToken) {
    await detectOnce(token);
    if (token !== runToken) return;
    // Even at PACE_MS.fast (0) this yields to the event loop, so the detection loop can never
    // starve rendering or input.
    await sleep(paceMs);
  }
}

/**
 * Open the camera and begin publishing observations. Idempotent, and safe to call before the
 * models exist — it loads them first, which is why status passes through 'starting'.
 */
export async function startFaceWatch() {
  if (status === "watching" || status === "starting") return;
  if (suspendCount > 0) return;
  setState("starting");
  const token = (runToken += 1);

  try {
    await loadFaceEngine();
    if (token !== runToken) return;

    stream = await openFaceCameraStream();
    // The user can revoke access, or enrollment can claim the camera, while getUserMedia is in
    // flight — in which case this stream is already orphaned and must not be kept.
    if (token !== runToken) {
      for (const track of stream.getTracks()) track.stop();
      stream = null;
      return;
    }

    const element = ensureVideoElement();
    element.srcObject = stream;
    await element.play();
    if (token !== runToken) return;

    setState("watching");
    void runDetectLoop(token);
  } catch (error) {
    console.error("[Face] failed to start watching:", error);
    releaseCamera();
    // NotAllowedError is the user (or the OS) refusing the camera — a settings problem, not a
    // bug, and the settings page says so instead of showing a raw error string.
    const denied = error?.name === "NotAllowedError" || error?.name === "SecurityError";
    setState("error", denied ? "camera-denied" : "camera-unavailable");
  }
}

/** Stop watching and release the camera. Idempotent. */
export function stopFaceWatch() {
  // Bumping the token is the whole stop: runDetectLoop checks it every pass and returns.
  runToken += 1;
  releaseCamera();
  lastObservation = null;
  setState("off");
}

/**
 * How hard to work. The policy decides this from the scene (see applyPace) — "calm" only while
 * the owner is sat at their own session, "fast" whenever someone may be waiting on a decision.
 * The running loop picks it up on its next pass — no restart, no camera flicker.
 */
export function setFaceWatchPace(pace) {
  paceMs = pace === "fast" ? PACE_MS.fast : PACE_MS.calm;
}

/** Current gap between detections, in ms. Read-only; for diagnostics. */
export function getFaceWatchPaceMs() {
  return paceMs;
}

/**
 * Hand the camera to the enrollment dialog for the duration of its capture. Reference-counted
 * so overlapping callers can't resume it early. resumeFaceWatch does NOT restart on its own —
 * it asks the policy to re-evaluate, which restarts only if the feature is still enabled (the
 * user may have just turned it off from the very dialog that suspended it).
 */
export function suspendFaceWatch() {
  suspendCount += 1;
  if (suspendCount === 1) stopFaceWatch();
}

export function resumeFaceWatch() {
  suspendCount = Math.max(0, suspendCount - 1);
  if (suspendCount === 0) {
    emit(STATE_CHANGE_EVENT, { status, errorReason });
  }
}

export function isFaceWatchSuspended() {
  return suspendCount > 0;
}
